import { useState } from 'react';
import { Flex, Text, IconButton, Popover, List, iconWeight, Icon } from '@nula/core-ui';
import clsx from 'clsx';
import type { ContextDoc, UseAssistantContextReturn } from '../hooks/use-assistant-context';
import styles from './assistant-context-bar.module.css';

export type { ContextDoc };

interface AssistantContextBarProps extends UseAssistantContextReturn {
  /** Extra CSS class for the root Flex row */
  className?: string;
  /** Disable interactions while agent is running */
  isRunActive?: boolean;
}

export function AssistantContextBar({
  className,
  contextDocs,
  filteredAvailable,
  docSearch,
  setDocSearch,
  addContext,
  removeContext,
  clearContext,
  isRunActive: _isRunActive = false,
}: AssistantContextBarProps) {
  const [open, setOpen] = useState(false);

  const hasContext = contextDocs.length > 0;

  const popoverContent = (
    <Flex vertical className={styles.popoverContent}>
      {/* Pinned docs list — shown only when context is non-empty */}
      {hasContext && (
        <Flex vertical className={styles.pinnedSection}>
          <Flex align="center" justify="space-between" className={styles.pinnedHeader}>
            <Text size="xs" type="secondary" className={styles.sectionLabel}>In context</Text>
            <IconButton size="sm" aria-label="Clear context" onClick={clearContext} className={styles.clearBtn}>
              <Icon name="close" size={12} />
            </IconButton>
          </Flex>
          <List
            size="small"
            dataSource={contextDocs}
            renderItem={(ctx) => (
              <List.Item
                className={styles.pinnedItem}
                extra={
                  ctx.isActive ? null : (
                    <IconButton
                      size="sm"
                      onClick={() => removeContext(ctx.id)}
                      aria-label="Remove document from context"
                      className={styles.removeBtn}
                    >
                      <Icon name="close" size={12} />
                    </IconButton>
                  )
                }
              >
                <Flex align="center" gap="xs" className={styles.docRow}>
                  <Icon name={ctx.isActive ? 'edit_document' : 'description'} size={12} className={styles.docIcon} />
                  <Text size="sm" className={styles.docLabel}>{ctx.title}</Text>
                  {ctx.isActive && <Text size="xs" type="secondary" italic>current</Text>}
                </Flex>
              </List.Item>
            )}
          />
          <div className={styles.divider} />
        </Flex>
      )}

      {/* Search / add section — always shown */}
      <Flex align="center" gap="xs" className={styles.searchRow}>
        <Icon name="search" size={12} className={styles.searchIcon} />
        <input
          autoFocus
          value={docSearch}
          onChange={(e) => setDocSearch(e.target.value)}
          placeholder="Search documents..."
          className={styles.searchInput}
        />
      </Flex>
      <Flex vertical className={styles.searchList}>
        {filteredAvailable.length === 0 ? (
          <Text type="secondary" size="sm" className={styles.emptyText}>
            No documents found
          </Text>
        ) : (
          <List
            size="small"
            dataSource={filteredAvailable}
            renderItem={(doc) => (
              <List.Item
                className={styles.searchItem}
                onClick={() => {
                  addContext(doc.id);
                  setOpen(false);
                  setDocSearch('');
                }}
              >
                <Flex align="center" gap="xs" className={styles.docRow}>
                  <Icon name="description" size={12} className={styles.docIcon} />
                  <Text size="sm" className={styles.docLabel}>{doc.title}</Text>
                </Flex>
              </List.Item>
            )}
          />
        )}
      </Flex>
    </Flex>
  );

  return (
    <Flex align="center" className={clsx(styles.root, className)}>
      <Popover
        trigger="click"
        placement="top-start"
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setDocSearch(''); }}
        content={popoverContent}
      >
        <div className={styles.triggerWrapper}>
          <IconButton
            size="sm"
            aria-label="Context"
            active={hasContext}
            className={clsx(styles.btn, hasContext && styles.btnActive)}
          >
            <Icon name="library_books" size={14} weight={iconWeight.light} />
          </IconButton>
          {hasContext && (
            <span className={styles.badge}>{contextDocs.length}</span>
          )}
        </div>
      </Popover>

      {/* Model picker hidden — server resolves models via caller_profile. Re-enable by rendering <ModelSelector disabled={isRunActive} /> here. */}
    </Flex>
  );
}
